import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Button,
  Card,
  CardContent,
  Divider,
  Tooltip
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  SportsEsports as GamesIcon,
  Devices as ConsolesIcon
} from '@mui/icons-material';
import axiosInstance from '../axiosInstance';
import { useSnackbar } from 'notistack';

/**
 * Component to display a summary of the total value of a user's collection
 * @param {Object} props - Component props
 * @param {string} props.userId - User ID
 */
const CollectionValueSummary = ({ userId }) => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const fetchCollectionValue = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch games and consoles for the user
      const [gamesResponse, consolesResponse] = await Promise.all([
        axiosInstance.get(`/games/user/${userId}`),
        axiosInstance.get(`/consoles/user/${userId}`)
      ]);
      
      const games = gamesResponse.data;
      const consoles = consolesResponse.data;
      
      // Calculate total buy and sell values
      let totalBuyValue = 0;
      let totalSellValue = 0;
      let gamesWithPrices = 0;
      let consolesWithPrices = 0;
      
      // Process games
      games.forEach(game => {
        if (game.price && game.price.includes('CEX:')) {
          try {
            // Extract buy and sell values from the price string
            const buyMatch = game.price.match(/£(\d+(\.\d+)?)\s*\(Buy\)/);
            const sellMatch = game.price.match(/£(\d+(\.\d+)?)\s*\(Sell\)/);
            
            if (buyMatch && sellMatch) {
              const buyValue = parseFloat(buyMatch[1]);
              const sellValue = parseFloat(sellMatch[1]);
              
              if (!isNaN(buyValue) && !isNaN(sellValue)) {
                totalBuyValue += buyValue;
                totalSellValue += sellValue;
                gamesWithPrices++;
              }
            }
          } catch (err) {
            console.error('Error parsing game price:', err);
          }
        }
      });
      
      // Process consoles
      consoles.forEach(console => {
        if (console.price && console.price.includes('CEX:')) {
          try {
            // Extract buy and sell values from the price string
            const buyMatch = console.price.match(/£(\d+(\.\d+)?)\s*\(Buy\)/);
            const sellMatch = console.price.match(/£(\d+(\.\d+)?)\s*\(Sell\)/);
            
            if (buyMatch && sellMatch) {
              const buyValue = parseFloat(buyMatch[1]);
              const sellValue = parseFloat(sellMatch[1]);
              
              if (!isNaN(buyValue) && !isNaN(sellValue)) {
                totalBuyValue += buyValue;
                totalSellValue += sellValue;
                consolesWithPrices++;
              }
            }
          } catch (err) {
            console.error('Error parsing console price:', err);
          }
        }
      });
      
      // Set summary data
      setSummary({
        totalGames: games.length,
        totalConsoles: consoles.length,
        gamesWithPrices,
        consolesWithPrices,
        totalItems: games.length + consoles.length,
        itemsWithPrices: gamesWithPrices + consolesWithPrices,
        totalBuyValue: totalBuyValue.toFixed(2),
        totalSellValue: totalSellValue.toFixed(2),
        averageBuyValue: gamesWithPrices + consolesWithPrices > 0 
          ? (totalBuyValue / (gamesWithPrices + consolesWithPrices)).toFixed(2) 
          : '0.00',
        averageSellValue: gamesWithPrices + consolesWithPrices > 0 
          ? (totalSellValue / (gamesWithPrices + consolesWithPrices)).toFixed(2) 
          : '0.00'
      });
    } catch (err) {
      console.error('Error fetching collection value:', err);
      setError('Failed to fetch collection value. Please try again.');
      enqueueSnackbar('Failed to fetch collection value: ' + (err.response?.data?.error || err.message), { 
        variant: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (userId) {
      fetchCollectionValue();
    }
  }, [userId]);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#ffebee' }}>
        <Typography color="error">{error}</Typography>
        <Button 
          startIcon={<RefreshIcon />}
          variant="outlined" 
          color="primary"
          onClick={fetchCollectionValue}
          sx={{ mt: 2 }}
        >
          Try Again
        </Button>
      </Paper>
    );
  }
  
  if (!summary) {
    return null;
  }
  
  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
            <MoneyIcon sx={{ mr: 1 }} />
            Collection Value Summary
          </Typography>
          <Button 
            startIcon={<RefreshIcon />}
            size="small"
            onClick={fetchCollectionValue}
          >
            Refresh
          </Button>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {/* Collection Stats */}
          <Paper sx={{ p: 2, flex: '1 1 300px', bgcolor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>Collection Stats</Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <GamesIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography>
                Games: {summary.totalGames} ({summary.gamesWithPrices} with CEX value)
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ConsolesIcon sx={{ mr: 1, color: 'secondary.main' }} />
              <Typography>
                Consoles: {summary.totalConsoles} ({summary.consolesWithPrices} with CEX value)
              </Typography>
            </Box>
            
            <Typography>
              Total Items: {summary.totalItems} ({summary.itemsWithPrices} with CEX value)
            </Typography>
          </Paper>
          
          {/* Value Summary */}
          <Paper sx={{ p: 2, flex: '1 1 300px', bgcolor: '#e8f5e9' }}>
            <Typography variant="h6" gutterBottom>CEX Market Value</Typography>
            
            <Tooltip title="The total amount you would pay to buy your entire collection from CEX">
              <Typography sx={{ mb: 1 }}>
                <strong>Total Buy Value:</strong> £{summary.totalBuyValue}
              </Typography>
            </Tooltip>
            
            <Tooltip title="The total amount you would receive if you sold your entire collection to CEX">
              <Typography sx={{ mb: 1 }}>
                <strong>Total Sell Value:</strong> £{summary.totalSellValue}
              </Typography>
            </Tooltip>
            
            <Tooltip title="Average value per item with CEX pricing">
              <Typography>
                <strong>Average Item Value:</strong> £{summary.averageBuyValue} (Buy) / £{summary.averageSellValue} (Sell)
              </Typography>
            </Tooltip>
          </Paper>
        </Box>
        
        {summary.itemsWithPrices === 0 && (
          <Typography sx={{ mt: 2, color: 'text.secondary', fontStyle: 'italic' }}>
            No items with CEX market values found. Use the "Update Price" button in the CEX Market Value section 
            of your games and consoles to add market values to your collection.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default CollectionValueSummary;
